import { useEffect, useRef, useState } from "react";
import { Handshake, ArrowRight, Brain, Users, Shield, Quote, ExternalLink, Cpu, Sparkles, ChevronRight, FileText, Calendar, Globe } from "lucide-react";
import { Link } from "react-router-dom";

function useScrollReveal(ref) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) entry.target.querySelectorAll(".animate-on-scroll, .slide-left, .slide-right, .scale-in, .blur-in, .stagger-item, .line-animate").forEach((el) => el.classList.add("visible")); }),
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
}

const AI_FEATURES = [
  { icon: Brain, title: "Scenario-Based Training", desc: "Interactive AI simulations that mirror real caregiving situations — from crisis de-escalation to trauma-informed communication." },
  { icon: Shield, title: "Trauma-Informed Approaches", desc: "Modules built on evidence-based practices to help caregivers respond with empathy and skill to complex emotional needs." },
  { icon: Users, title: "Scalable & Accessible", desc: "Digital tools that reach caregivers everywhere — breaking down barriers to quality training regardless of location or resources." },
];

export default function CommunityPartnerships() {
  const s1 = useRef(null);
  const s2 = useRef(null);
  const s3 = useRef(null);
  const s4 = useRef(null);
  const s5 = useRef(null);
  const [activeFeature, setActiveFeature] = useState(0);
  useScrollReveal(s1);
  useScrollReveal(s2);
  useScrollReveal(s3);
  useScrollReveal(s4);
  useScrollReveal(s5);

  return (
    <main className="pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#1A3A4A] to-[#1A3A4A]/90 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[300px] h-[300px] bg-[#3DAA35]/15 rounded-full -translate-x-1/3 -translate-y-1/3 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[250px] h-[250px] bg-[#0E8DB5]/15 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div ref={s1}>
            <div className="scale-in max-w-3xl">
              <p className="text-xs font-sans tracking-[0.25em] uppercase font-semibold text-[#7CC8E0] mb-4">
                <span className="inline-block w-10 h-[2px] bg-[#3DAA35] mr-3 align-middle" />Growing Together
              </p>
              <h1 data-testid="community-heading" className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-tight mb-6">
                Community <span className="text-[#7CC8E0]">Partnerships</span>
              </h1>
              <p className="text-base sm:text-lg font-sans font-medium text-white/75 max-w-2xl">
                Real change happens when organizations unite. TYSS is partnering with innovators to transform how caregivers are supported.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gradient divider */}
      <div className="h-1 bg-gradient-to-r from-[#0E8DB5] via-[#3DAA35] to-[#0E8DB5]" />

      {/* Partnership Announcement - Hero Card */}
      <section ref={s2} className="py-24 bg-[#F8FAFB] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#0E8DB5]/3 rounded-full translate-x-1/2 -translate-y-1/2 blur-[100px]" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          {/* Announcement badge */}
          <div className="scale-in flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0E8DB5]/10 border border-[#0E8DB5]/20">
              <Sparkles className="w-4 h-4 text-[#0E8DB5] icon-float" />
              <span className="text-xs font-sans font-semibold tracking-wider uppercase text-[#0E8DB5]">New Partnership Announcement</span>
            </div>
          </div>

          {/* Main headline */}
          <div className="scale-in text-center mb-16">
            <h2 data-testid="partnership-heading" className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-earth leading-tight max-w-4xl mx-auto">
              TYSS Partners with <span className="gradient-text">Thrive Digital Health</span> to Advance AI-Based Caregiver Training
            </h2>
          </div>

          {/* Two-column layout */}
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left: Story */}
            <div>
              <p className="slide-left text-base sm:text-lg font-sans font-medium leading-relaxed text-earth-muted mb-5">
                TYSS is collaborating with <strong className="text-earth">Thrive Digital Health</strong> to shape the next generation of caregiver training — AI-powered simulations that help caregivers practice de-escalation, trauma-informed communication, and crisis response in realistic scenarios.
              </p>
              <p className="slide-left delay-100 text-base font-sans leading-relaxed text-earth-muted mb-8">
                This isn't theoretical. These are practical, interactive tools designed around the real challenges caregivers face every day — built with direct input from TYSS's lived experience and clinical expertise.
              </p>

              {/* Quote callout */}
              <div className="slide-left delay-200 p-6 rounded-2xl bg-gradient-to-r from-[#0E8DB5]/5 to-[#3DAA35]/5 border-l-4 border-[#0E8DB5] mb-8">
                <Quote className="w-6 h-6 text-[#0E8DB5] mb-3" />
                <p className="text-base font-sans italic leading-relaxed text-earth-muted mb-3">
                  "Caregivers are on the front line supporting youth with trauma, but many lack access to practical training. By working with Thrive Digital Health, we're ensuring new technology reflects real-world challenges and supports better outcomes for children and families."
                </p>
                <p className="text-sm font-sans font-bold text-earth">— Cynthia Reeves, Founder-CEO of TYSS</p>
              </div>

              <div className="slide-left delay-300 flex flex-wrap gap-3">
                <a href="https://20d0a0a2-9bc3-4878-a451-dbaa4c9318b7.paylinks.godaddy.com/bfd32eeb-318a-425f-b9fb-26a" target="_blank" rel="noopener noreferrer"
                  className="btn-pill btn-primary no-underline text-sm">
                  <Handshake className="w-4 h-4" /> Support This Initiative
                </a>
                <a href="https://www.ThriveDigitalHealth.us" target="_blank" rel="noopener noreferrer"
                  data-testid="thrive-link"
                  className="btn-pill btn-outline-dark no-underline text-sm">
                  <ExternalLink className="w-4 h-4" /> Visit Thrive Digital Health
                </a>
              </div>
            </div>

            {/* Right: Interactive feature cards */}
            <div className="slide-right">
              <div className="space-y-4">
                {AI_FEATURES.map((feature, i) => (
                  <button
                    key={feature.title}
                    data-testid={`ai-feature-${i}`}
                    onClick={() => setActiveFeature(i)}
                    className={`w-full text-left p-6 rounded-2xl border transition-all duration-500 cursor-pointer ${
                      activeFeature === i
                        ? "bg-white border-[#0E8DB5]/30 shadow-lg shadow-[#0E8DB5]/10 -translate-y-1"
                        : "bg-white/50 border-[#C8DDD8]/50 hover:border-[#0E8DB5]/20 hover:bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        activeFeature === i ? "bg-[#0E8DB5] shadow-md shadow-[#0E8DB5]/30" : "bg-[#0E8DB5]/10"
                      }`}>
                        <feature.icon className={`w-6 h-6 transition-colors duration-300 ${
                          activeFeature === i ? "text-white" : "text-[#0E8DB5]"
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className={`text-lg font-serif font-bold transition-colors duration-300 ${
                            activeFeature === i ? "text-[#0E8DB5]" : "text-earth"
                          }`}>{feature.title}</h4>
                          <ChevronRight className={`w-4 h-4 transition-all duration-300 ${
                            activeFeature === i ? "text-[#0E8DB5] rotate-90" : "text-earth-muted/30"
                          }`} />
                        </div>
                        <div className={`overflow-hidden transition-all duration-500 ${
                          activeFeature === i ? "max-h-40 opacity-100 mt-2" : "max-h-0 opacity-0"
                        }`}>
                          <p className="text-sm font-sans text-earth-muted leading-relaxed">{feature.desc}</p>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* AI visualization */}
              <div className="mt-6 p-6 rounded-2xl bg-gradient-to-br from-[#1A3A4A] to-[#0E8DB5]/80 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                <div className="relative z-10 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center icon-float-slow">
                    <Cpu className="w-7 h-7 text-[#7CC8E0]" />
                  </div>
                  <div>
                    <p className="text-sm font-sans font-semibold text-white/90">AI-Powered Training</p>
                    <p className="text-xs font-sans text-white/60">Interactive simulations that adapt to each caregiver's learning pace</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Press Release */}
      <section ref={s5} className="py-20 md:py-24 bg-white relative overflow-hidden">
        <div className="absolute top-20 left-0 w-[300px] h-[300px] bg-[#3DAA35]/5 rounded-full -translate-x-1/2 blur-3xl" />
        <div className="absolute bottom-20 right-0 w-[300px] h-[300px] bg-[#0E8DB5]/5 rounded-full translate-x-1/2 blur-3xl" />

        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          {/* Press release badge */}
          <div className="flex justify-center mb-8 scale-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3DAA35]/10 border border-[#3DAA35]/20">
              <FileText className="w-4 h-4 text-[#3DAA35]" />
              <span className="text-xs font-sans font-semibold tracking-wider uppercase text-[#3DAA35]">Press Release · Full Announcement</span>
            </div>
          </div>

          {/* Press Release Card */}
          <article className="scale-in delay-100 bg-[#F8FAFB] border border-[#C8DDD8]/60 rounded-3xl p-8 md:p-12 shadow-sm">
            {/* Article header */}
            <header className="border-b border-[#C8DDD8]/60 pb-6 mb-8">
              <h2 data-testid="press-release-heading" className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold tracking-tight text-earth leading-tight mb-4">
                TYSS, Inc. Partners with <span className="gradient-text">Thrive Digital Health</span>
              </h2>
              <div className="flex flex-wrap items-center gap-4 text-xs font-sans text-earth-muted">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Recent Announcement
                </span>
                <span className="w-1 h-1 rounded-full bg-earth-muted/40" />
                <span className="inline-flex items-center gap-1.5">
                  <Handshake className="w-3.5 h-3.5" /> Strategic Partnership
                </span>
                <span className="w-1 h-1 rounded-full bg-earth-muted/40" />
                <span className="inline-flex items-center gap-1.5">
                  <Brain className="w-3.5 h-3.5" /> AI Caregiver Training
                </span>
              </div>
            </header>

            {/* Article body */}
            <div className="space-y-6 text-base sm:text-lg font-sans leading-relaxed text-earth-muted">
              <p>
                TYSS recently announced a new partnership with <strong className="text-earth">Thrive Digital Health</strong> to contribute subject matter expertise to the development of artificial intelligence (AI)–based caregiver training modules.
              </p>

              <p>
                Through this collaboration, TYSS will provide input and practical insight to help shape training simulations designed to strengthen caregiver skills and improve treatment plan compliance for youth experiencing trauma and other mental health challenges.
              </p>

              <p>
                The AI-based modules being developed by Thrive Digital Health use interactive, scenario-based training to help caregivers practice communication strategies, de-escalation techniques, and trauma-informed approaches in realistic situations. The goal is to better prepare caregivers, families, and support professionals to respond effectively to complex behavioral and emotional needs.
              </p>

              {/* Pull quote */}
              <blockquote className="my-8 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-[#0E8DB5]/8 to-[#3DAA35]/8 border-l-4 border-[#0E8DB5] relative">
                <Quote className="w-8 h-8 text-[#0E8DB5]/40 absolute top-4 right-4" />
                <p className="text-base sm:text-lg font-serif italic leading-relaxed text-earth mb-4 pr-8">
                  "Caregivers are often on the front line supporting youth with trauma, but many lack access to practical training tools. By working with Thrive Digital Health, we can help ensure that new technology reflects real-world caregiving challenges and supports better outcomes for children and families."
                </p>
                <footer className="text-sm font-sans not-italic">
                  <p className="font-bold text-earth">Cynthia Reeves</p>
                  <p className="text-earth-muted">Founder-CEO of Trauma Youth Support Services<br />Caregiver and Youth Intensive Care Coordinator (ICC)</p>
                </footer>
              </blockquote>

              <p>
                Thrive Digital Health is developing scalable digital training solutions that use AI-driven role-playing scenarios to help caregivers and behavioral health professionals build critical interpersonal and decision-making skills.
              </p>

              <p>
                This partnership reflects a shared commitment to improving the quality and accessibility of caregiver training resources that support youth mental health and trauma recovery.
              </p>
            </div>

            {/* Article footer / CTA */}
            <footer className="mt-10 pt-8 border-t border-[#C8DDD8]/60">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0E8DB5]/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-[#0E8DB5]" />
                  </div>
                  <div>
                    <p className="text-xs font-sans font-semibold tracking-wider uppercase text-earth-muted mb-0.5">For More Information</p>
                    <p className="text-sm font-sans font-medium text-earth">Visit Thrive Digital Health</p>
                  </div>
                </div>
                <a
                  href="https://www.ThriveDigitalHealth.us"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="press-release-thrive-link"
                  className="btn-pill btn-primary no-underline text-sm whitespace-nowrap"
                >
                  www.ThriveDigitalHealth.us <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </footer>
          </article>
        </div>
      </section>

      {/* Impact Stats */}
      <section ref={s3} className="py-16 bg-bone">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { value: "501(c)(3)", label: "Registered Nonprofit" },
              { value: "AI-Powered", label: "Training Innovation" },
              { value: "24/7", label: "Accessible Anywhere" },
            ].map((stat, i) => (
              <div key={stat.label} className={`stagger-item text-center p-8 rounded-2xl bg-white border border-[#C8DDD8]/50 card-lift`} style={{ transitionDelay: `${i * 0.1}s` }}>
                <p className="text-3xl font-serif font-bold shimmer-text mb-2">{stat.value}</p>
                <p className="text-sm font-sans font-semibold text-earth-muted tracking-wider uppercase">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Partnerships */}
      <section ref={s4} className="py-20 bg-[#F8FAFB]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="scale-in">
            <div className="w-16 h-16 rounded-full bg-[#3DAA35]/10 flex items-center justify-center mx-auto mb-6 icon-float">
              <Handshake className="w-8 h-8 text-[#3DAA35]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold tracking-tight text-earth mb-4">
              More Partnerships <span className="gradient-text">Coming Soon</span>
            </h2>
            <p className="animate-on-scroll delay-100 text-base font-sans font-medium text-earth-muted leading-relaxed mb-8">
              We're building meaningful connections with organizations across Massachusetts and beyond. If your organization shares our commitment to supporting families in crisis, we'd love to hear from you.
            </p>
            <Link to="/contact" data-testid="community-contact-link" className="animate-on-scroll delay-200 inline-flex items-center gap-2 btn-pill btn-green no-underline">
              <Handshake className="w-4 h-4" /> Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
